import React, { useCallback, useEffect, useRef, useState } from "react";
import { COMPAT, checkCompatibility, shouldBlock } from "@/lib/release";
import { RefreshCw, Loader2 } from "lucide-react";

function isNativePlatform() {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

// Full-screen, professional block shown ONLY on a confirmed version incompatibility. It exposes no
// SHAs, environment or technical diagnostics, and never destroys locally stored data.
function UpdateRequired({ native, checking, onRetry }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-50 p-6" data-testid="release-blocked">
      <div className="cc-card max-w-md w-full p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <RefreshCw className="text-amber-600" size={22} />
        </div>
        <h2 className="font-display text-xl font-bold text-slate-800">Update required</h2>
        <p className="mt-2 text-sm text-slate-500">
          CaterCTRL needs to be updated before it can connect to this server.
        </p>
        <p className="mt-2 text-sm text-slate-500">
          {native
            ? "Please update to the latest version of the CaterCTRL app, then try again."
            : "Please reload the page to get the latest version of CaterCTRL."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {!native && (
            <button
              type="button"
              onClick={() => { try { window.location.reload(); } catch { /* noop */ } }}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white"
              data-testid="release-reload"
            >
              Reload
            </button>
          )}
          <button
            type="button"
            onClick={onRetry}
            disabled={checking}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60"
            data-testid="release-retry"
          >
            {checking ? <Loader2 className="animate-spin" size={16} /> : "Try again"}
          </button>
        </div>
      </div>
    </div>
  );
}

// One-shot compatibility check at startup. Runs ONCE (guarded by a ref) and only re-runs on an
// explicit user retry — never a polling/refresh loop. Result is held for the session.
export default function ReleaseGate({ children }) {
  const [state, setState] = useState(COMPAT.CHECKING);
  const ran = useRef(false);

  const runCheck = useCallback(async () => {
    const next = await checkCompatibility();
    setState(next);
  }, []);

  useEffect(() => {
    if (ran.current) return; // strictly one automatic check
    ran.current = true;
    runCheck();
  }, [runCheck]);

  const retry = useCallback(() => {
    setState(COMPAT.CHECKING);
    runCheck();
  }, [runCheck]);

  if (shouldBlock(state)) {
    return <UpdateRequired native={isNativePlatform()} checking={false} onRetry={retry} />;
  }
  return children;
}
